import * as React from "react";
import { useId } from "@utdanningsdirektoratet/utils/useId";
import { onKeyDown } from "@utdanningsdirektoratet/utils/uu";
import { SearchInput } from "@utdanningsdirektoratet/searchinput";
import { Tooltip } from "@utdanningsdirektoratet/tooltip";
import { cls } from "@utdanningsdirektoratet/utils/cls";
import { Icon } from "@utdanningsdirektoratet/icon";
import AnimateHeight from "react-animate-height";
import { isEmpty } from "@utdanningsdirektoratet/utils/string";
import Group from "./Group";
import CheckboxGroup from "./CheckboxGroup";
import Options from "./Options";
import styles from "./MultiSelect.module.less";

export type MultiSelectOption<T> = {
  text: string;
  value: T;
  disabled?: boolean;
  key?: string;
};

export type SimpleSelect<T> = {
  options: Array<MultiSelectOption<T>>;
  startExpanded?: undefined;
};

export type NestedMultiSelectOption<T> = {
  value: T;
  disabled?: boolean;
  options?: Array<MultiSelectOption<T>>;
};

export type GroupedSelect<T> = {
  options: Record<
    string,
    Array<MultiSelectOption<T>> | NestedMultiSelectOption<T>
  >;
  startExpanded?: boolean;
};

export type MultiSelectProps<T> = {
  id?: string;
  className?: string;
  arrowClassName?: string;
  withSearch?: boolean;
  disabled?: boolean;
  error?: boolean;
  message?: React.ReactNode;
  label: string;
  placeholder?: string;
  disableOnClickOutside?: boolean;
  onChange: (values: Array<T>) => void;
  value?: Array<T>;
  onFocus?: () => void;
  onBlur?: (values: Array<T>) => void;
  "aria-label"?: string;
} & (SimpleSelect<T> | GroupedSelect<T>);

const MultiSelect = <T,>({
  className,
  arrowClassName,
  label,
  placeholder,
  options = [],
  error,
  message,
  withSearch,
  onChange,
  value = [],
  onFocus,
  onBlur,
  disableOnClickOutside = false,
  disabled,
  startExpanded,
  ...rest
}: MultiSelectProps<T>): React.ReactElement => {
  const id = useId(rest.id);
  const [search, setSearch] = React.useState<string>("");
  const [isOpen, setIsOpen] = React.useState(false);
  const [isExpanded, setExpanded] = React.useState<Record<string, boolean>>({});
  const [focusedIndex, setFocusedIndex] = React.useState<string | null>(null);
  const focusedRef = React.useRef<HTMLInputElement>(null);
  const [isFocused, setIsFocused] = React.useState(false);
  const ref = React.useRef<HTMLDivElement>(null);

  const indexes = React.useMemo(() => {
    if (Array.isArray(options)) {
      return options
        .filter(
          (option) =>
            isEmpty(search) ||
            option.text.toLowerCase().indexOf(search.toLowerCase()) !== -1,
        )
        .map((_, index) => `${index}`);
    }
    return Object.keys(options)
      .reduce<Array<string | null>>((acc, key, index) => {
        if (Array.isArray(options[key])) {
          const hasCheckedOptions =
            startExpanded || isExpanded[key] || !isEmpty(search);
          return [
            ...acc,
            `${index}`,
            ...(hasCheckedOptions
              ? (options[key] as Array<MultiSelectOption<T>>)
                  .filter(
                    (option) =>
                      isEmpty(search) ||
                      option.text
                        .toLowerCase()
                        .indexOf(search.toLowerCase()) !== -1,
                  )
                  .map((_, _index) => `${index}-${_index}`)
              : []),
          ];
        }
        const hasSearchOptions =
          search &&
          (key.toLowerCase().indexOf(search.toLowerCase()) !== -1 ||
            ((options[key] as NestedMultiSelectOption<T>).options ?? []).filter(
              (option) =>
                isEmpty(search) ||
                option.text.toLowerCase().indexOf(search.toLowerCase()) !== -1,
            ).length > 0);
        const hasCheckedOptions =
          value.some(
            (v) =>
              ((options[key] as NestedMultiSelectOption<T>).options ?? [])
                .map((option) => option.value)
                .indexOf(v) !== -1,
          ) ||
          value.some(
            (v) => v === (options[key] as NestedMultiSelectOption<T>).value,
          ) ||
          startExpanded ||
          isExpanded[key];
        return [
          ...acc,
          hasSearchOptions || isEmpty(search) ? `${index}` : null,
          ...(hasCheckedOptions || hasSearchOptions
            ? ((options[key] as NestedMultiSelectOption<T>).options ?? [])
                .filter(
                  (option) =>
                    isEmpty(search) ||
                    key.toLowerCase().indexOf(search.toLowerCase()) !== -1 ||
                    option.text.toLowerCase().indexOf(search.toLowerCase()) !==
                      -1,
                )
                .map((_, _index) => `${index}-${_index}`)
            : []),
        ];
      }, [])
      .filter((i) => i);
  }, [options, value, startExpanded, isExpanded, search]);

  const onCheckboxChange = (
    valueToAdd: Array<T> = [],
    valuesToRemove: Array<T> = [],
  ) => {
    onChange([
      ...value.filter((v) => valuesToRemove.indexOf(v) === -1),
      ...valueToAdd,
    ]);
  };

  const onFocusWrapped = (): void => {
    if (onFocus) {
      onFocus();
    }
    setIsFocused(true);
  };

  const onBlurWrapped = (): void => {
    if (onBlur) {
      onBlur(value);
    }
    setIsFocused(false);
  };

  React.useEffect(() => {
    if (!isEmpty(search)) setIsOpen(true);
  }, [search]);

  React.useEffect(() => {
    const handleClickOutside = (e: MouseEvent): void => {
      if (
        !disableOnClickOutside &&
        !ref.current?.contains(e.target as Element)
      ) {
        setIsFocused(false);
        setIsOpen(false);
        setSearch("");
      }
    };

    const handleKeyDown = (e: KeyboardEvent): void => {
      if (e.key === "Escape") {
        setIsOpen(false);
        setFocusedIndex(null);
        setSearch("");
      }

      if (e.key === "ArrowDown") {
        e.preventDefault();
        setIsOpen(true);
        setFocusedIndex(
          (prevState) =>
            indexes[
              Math.min(
                indexes.findIndex((r) => r === prevState) + 1,
                indexes.length - 1,
              )
            ],
        );
      }

      if (e.key === "Home") {
        e.preventDefault();
        setFocusedIndex(indexes[0]);
      }

      if (e.key === "End") {
        e.preventDefault();
        setFocusedIndex(indexes[indexes.length - 1]);
      }

      if (e.key === "ArrowUp") {
        e.preventDefault();
        setIsOpen(true);
        setFocusedIndex(
          (prevState) =>
            indexes[Math.max(indexes.findIndex((r) => r === prevState) - 1, 0)],
        );
      }

      if (e.key === "Tab" && isOpen) {
        e.preventDefault();
        setIsOpen(false);
        setSearch("");
      }
    };

    if (isOpen) {
      document.addEventListener("mousedown", handleClickOutside);
      document.addEventListener("keydown", handleKeyDown);
    }

    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
      document.removeEventListener("keydown", handleKeyDown);
    };
  }, [isOpen, disableOnClickOutside, indexes]);

  React.useEffect(() => {
    if (focusedIndex) {
      if (!indexes.includes(focusedIndex)) setFocusedIndex(indexes[0]);
      setTimeout(() => {
        (focusedRef?.current as Element)?.scrollIntoView?.({
          behavior: "smooth",
          block: "center",
          inline: "nearest",
        });
      }, 250);
    }
  }, [focusedIndex, indexes]);

  React.useEffect(() => {
    if (disabled) {
      setIsOpen(false);
    }
  }, [disabled]);

  const onClick = (e: React.MouseEvent<HTMLElement>) => {
    if (!disabled && (e.target as HTMLElement).nodeName !== "INPUT") {
      setIsOpen((prevState) => !prevState);
    }
  };

  const onKeyDownWrapped = (
    event: React.KeyboardEvent<HTMLDivElement>,
  ): void => {
    if (isOpen) {
      if (event.key === "Spacebar" || event.key === " ") {
        if (focusedIndex) {
          event.preventDefault();
        }
        focusedRef?.current?.click();
      }
    } else {
      onKeyDown(onClick)(event);

      if (event.key === "ArrowDown" || event.key === "ArrowUp") {
        setIsOpen(true);
      }
    }
  };

  const getOptions = () => {
    if (Array.isArray(options)) {
      return (
        <Options
          id={id}
          focusedRef={focusedRef}
          focusedIndex={focusedIndex}
          options={options}
          search={search}
          values={value}
          disabled={disabled}
          onChange={onCheckboxChange}
        />
      );
    }
    return Object.keys(options).map((key, index) => {
      if (Array.isArray(options[key])) {
        return (
          <Group
            id={id}
            key={key}
            isExpanded={isExpanded[key]}
            setExpanded={setExpanded}
            title={key}
            options={options[key] as Array<MultiSelectOption<T>>}
            search={search}
            focusedRef={focusedRef}
            focusedIndex={focusedIndex}
            index={index}
            onChange={onCheckboxChange}
            values={value}
            disabled={disabled}
            expanded={startExpanded}
          />
        );
      }
      return (
        <CheckboxGroup
          id={id}
          key={key}
          title={key}
          isExpanded={isExpanded[key]}
          setExpanded={setExpanded}
          focusedRef={focusedRef}
          focusedIndex={focusedIndex}
          options={options[key] as NestedMultiSelectOption<T>}
          index={index}
          search={search}
          onChange={onCheckboxChange}
          values={value}
          disabled={disabled}
          expanded={startExpanded}
        />
      );
    });
  };

  const multiSelectClass = cls(
    {
      [styles.multiSelectContainer]: true,
    },
    className,
  );

  const labelClass = cls({
    [styles.multiSelectLabel]: true,
    [styles.multiSelectLabelFocused]: isOpen || isFocused,
  });

  const selectClass = cls({
    [styles.multiSelectSelect]: true,
    [styles.multiSelectSelectIsOpen]: isOpen || isFocused,
    [styles.multiSelectSelectError]: error,
    [styles.multiSelectSelectDisabled]: disabled,
    [styles.multiSelectSelectWithSearch]: withSearch,
  });

  const iconClass = cls(
    {
      [styles.multiSelectIcon]: true,
      [styles.multiSelectIconIsOpen]: isOpen,
      [styles.multiSelectIconDisabled]: disabled,
    },
    arrowClassName,
  );

  return (
    <div className={multiSelectClass} ref={ref}>
      {!withSearch && placeholder && label ? (
        <label
          className={labelClass}
          htmlFor={`${id}-multiselect`}
          id={`${id}-label`}
        >
          {label}
        </label>
      ) : null}
      <Tooltip
        id={!withSearch ? `${id}-multiselect` : undefined}
        className={selectClass}
        role="combobox"
        aria-expanded={isOpen}
        aria-owns={id}
        aria-haspopup="listbox"
        message={message}
        disabled={!error}
        alwaysShow={error}
        onClick={onClick}
        onKeyDown={!withSearch ? onKeyDownWrapped : undefined}
        onFocus={!withSearch ? onFocusWrapped : undefined}
        onBlur={!withSearch ? onBlurWrapped : undefined}
        aria-label={
          !withSearch && placeholder && (rest["aria-label"] || label)
            ? undefined
            : (rest["aria-label"] ?? label)
        }
        aria-labelledby={
          !withSearch && placeholder && label ? `${id}-label` : undefined
        }
        aria-activedescendant={
          !withSearch && focusedIndex ? `${id}-${focusedIndex}` : undefined
        }
        align="right"
        tabIndex={withSearch ? -1 : 0}
        alert
      >
        {withSearch ? (
          <SearchInput
            id={`${id}-multiselect`}
            label={label}
            className={styles.multiSelectSearchinputwrapper}
            onClick={onClick}
            onKeyDown={onKeyDownWrapped}
            onFocus={onFocusWrapped}
            onBlur={onBlurWrapped}
            onChange={setSearch}
            value={search}
            disabled={disabled}
            aria-controls={id}
            aria-autocomplete="list"
            aria-label={rest["aria-label"] ?? label}
            aria-labelledby={label ? `${id}-label` : undefined}
            aria-activedescendant={
              focusedIndex ? `${id}-${focusedIndex}` : undefined
            }
            withoutClear
          />
        ) : (
          (placeholder ?? label)
        )}
        <Icon className={iconClass} icon="angle down" />
      </Tooltip>
      <AnimateHeight
        className={styles.multiSelectDropdown}
        id={id}
        height={isOpen ? "auto" : 0}
        role="listbox"
        aria-multiselectable
        aria-hidden={!isOpen}
      >
        <div
          className={styles.multiSelectOptions}
          onMouseOver={() => setFocusedIndex(null)}
          onFocus={() => null}
        >
          {getOptions()}
        </div>
      </AnimateHeight>
    </div>
  );
};

export default MultiSelect;
